import { Temporal } from '@js-temporal/polyfill'
import { z } from 'zod'

export const CalendarPresetIdSchema = z.enum([
  'today',
  'last_7_days',
  'last_30_days',
  'month_to_date',
  'last_3_months',
  'year_to_date',
])

export type CalendarPresetId = z.infer<typeof CalendarPresetIdSchema>

export interface TemporalDateRange {
  from: Temporal.PlainDate | null
  to: Temporal.PlainDate | null
}

export interface TCalendarPreset {
  id: CalendarPresetId
  label: string
  value: {
    from: Temporal.PlainDate
    to: Temporal.PlainDate
  }
}

interface CalendarPresetDefinition {
  id: CalendarPresetId
  label: string
}

const CalendarPresetDefinitions: Array<CalendarPresetDefinition> = [
  {
    id: 'today',
    label: 'Today',
  },
  {
    id: 'last_7_days',
    label: 'Last 7 days',
  },
  {
    id: 'last_30_days',
    label: 'Last 30 days',
  },
  {
    id: 'month_to_date',
    label: 'Month to date',
  },
  {
    id: 'last_3_months',
    label: 'Last 3 months',
  },
  {
    id: 'year_to_date',
    label: 'Year to date',
  },
]

export function getCalendarPresetLabel(presetId: CalendarPresetId) {
  const definition = CalendarPresetDefinitions.find((presetDefinition) => {
    return presetDefinition.id === presetId
  })

  return definition?.label ?? presetId
}

export function resolveCalendarPresetDateRange({
  presetId,
  today = Temporal.Now.plainDateISO(),
}: {
  presetId: CalendarPresetId
  today?: Temporal.PlainDate
}) {
  if (presetId === 'today') {
    return {
      from: today,
      to: today,
    }
  }

  if (presetId === 'last_7_days') {
    return {
      from: today.subtract({ days: 7 }),
      to: today,
    }
  }

  if (presetId === 'last_30_days') {
    return {
      from: today.subtract({ days: 30 }),
      to: today,
    }
  }

  if (presetId === 'month_to_date') {
    return {
      from: today.with({ day: 1 }),
      to: today,
    }
  }

  if (presetId === 'last_3_months') {
    return {
      from: today.subtract({ months: 3 }),
      to: today,
    }
  }

  return {
    from: today.with({ day: 1, month: 1 }),
    to: today,
  }
}

function createCalendarPreset({
  definition,
  today,
}: {
  definition: CalendarPresetDefinition
  today: Temporal.PlainDate
}): TCalendarPreset {
  return {
    id: definition.id,
    label: definition.label,
    value: resolveCalendarPresetDateRange({
      presetId: definition.id,
      today,
    }),
  }
}

export function createLast3MonthsPreset(): TCalendarPreset {
  const today = Temporal.Now.plainDateISO()
  return createCalendarPreset({
    definition: {
      id: 'last_3_months',
      label: 'Last 3 months',
    },
    today,
  })
}

export function createDefaultCalendarPresets(): Array<TCalendarPreset> {
  const today = Temporal.Now.plainDateISO()
  return CalendarPresetDefinitions.map((definition) => {
    return createCalendarPreset({
      definition,
      today,
    })
  })
}
